// Derives a full tonal ramp from a single tenant brand color. Whatever shade is configured
// (backend theme_json.colors.primary), we normalize it to a canonical dark "base" — same
// hue/saturation, fixed lightness — rather than trusting the exact shade that was picked.
// Lighter/darker tones are then generated from that base, so buttons/accents stay legible
// no matter how light or dark the originally-configured color was.
import { darken, lighten, getContrastRatio, decomposeColor, hslToRgb, rgbToHex } from '@mui/material/styles'

export interface PrimaryPaletteTones {
  50: string
  100: string
  200: string
  300: string
  400: string
  500: string // base/main
  600: string // dark
  700: string
  800: string
  900: string
}

export interface PrimaryPalette {
  main: string
  light: string
  dark: string
  contrastText: string
  tones: PrimaryPaletteTones
}

// Target lightness (0-1) for the normalized base tone, regardless of the input shade's
// own lightness. Tuned to stay dark enough for legible button/accent contrast.
const BASE_LIGHTNESS = 0.32

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0)
      break
    case g:
      h = (b - r) / d + 2
      break
    default:
      h = (r - g) / d + 4
  }
  return [h * 60, s, l]
}

const hslToHex = (h: number, s: number, l: number): string => rgbToHex(hslToRgb(`hsl(${h}, ${s * 100}%, ${l * 100}%)`))

const deriveBaseHex = (inputColor: string): string => {
  const [r, g, b] = decomposeColor(inputColor).values
  const [h, s] = rgbToHsl(r, g, b)
  return hslToHex(h, s, BASE_LIGHTNESS)
}

// Minimum white-on-fill contrast ratio before we fall back to black text — same value as MUI's
// own `contrastThreshold` default, so our fills read the same way MUI's do.
const CONTRAST_THRESHOLD = 3

// White unless it drops below the threshold on this fill. A raw "bigger ratio wins" pick flips to
// black on mid-tone brands (teal, mid blue) where black scores higher but reads as unstyled text
// on a coloured chip.
const pickContrastText = (backgroundHex: string): string => (getContrastRatio(backgroundHex, '#fff') >= CONTRAST_THRESHOLD ? '#fff' : '#000')

export const buildPrimaryPalette = (inputColor: string): PrimaryPalette => {
  const base = deriveBaseHex(inputColor)
  const tones: PrimaryPaletteTones = {
    50: rgbToHex(lighten(base, 0.95)),
    100: rgbToHex(lighten(base, 0.85)),
    200: rgbToHex(lighten(base, 0.65)),
    300: rgbToHex(lighten(base, 0.45)),
    400: rgbToHex(lighten(base, 0.2)),
    500: base,
    600: rgbToHex(darken(base, 0.15)),
    700: rgbToHex(darken(base, 0.3)),
    800: rgbToHex(darken(base, 0.45)),
    900: rgbToHex(darken(base, 0.6)),
  }
  return {
    main: tones[500],
    light: tones[400],
    dark: tones[600],
    contrastText: pickContrastText(tones[500]),
    tones,
  }
}
